import { useEffect, useRef, useState } from 'react';
import { Dropdown } from '../dropdown';
import styles from './BulkActionDropdownNew.module.css';
import BulkActionEventManager, { BulkActionEventData } from './BulkActionEventManager'
import ButtonNew from '../../molecules/button/Button';
import { Trans, useTranslation } from 'react-i18next';
import SkeletonComponent from '../../../components/SkeletonComponent';
import ListingStatus from '../../../enums/listingStatus';
import UnarchiveListingModal from '../../../components/modals/unarchiveListingModal/UnarchiveListingModal';
import ArchiveListingModal from '../../../components/modals/archiveListingModal/ArchiveListingModal';
import ArrowDownSLineIcon from '../../atoms/icons/ArrowDownSLineIcon';
import { classNames } from '../../utils';
import Text from '../../atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../enums';

interface BulkActionDropownProps {
    readonly status: string;
}

const bulkActionDropdownNew = BulkActionEventManager.create<BulkActionEventData>('bulk-action');
type BulkActionDropdownComponent = (props: BulkActionDropownProps) => JSX.Element;

const BulkActionDropdownNew = ({
    status
}: BulkActionDropownProps) => {
    const { t } = useTranslation();

    const dropdownContentRef = useRef<HTMLDivElement>(null);
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);

    const [selectedListings, setSelectedListings] = useState<string[]>([]);
    const [isDropdownActive, setIsDropdownActive] = useState<boolean>(false);

    const updateSelectedListings = () => {
        setSelectedListings([...bulkActionDropdownNew.selectedListings]);
    }

    const handleDropdownButtonClick = () => {
        setIsDropdownActive(!isDropdownActive);
    }

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            const target = e.target as Node;

            const clickedInsideDropdown =
                dropdownContentRef.current?.contains(target) ||
                dropdownButtonRef.current?.contains(target);

            if (!clickedInsideDropdown) {
                setIsDropdownActive(false);
            }
        }

        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        }
    }, [])

    useEffect(() => {
        bulkActionDropdownNew?.addListener(updateSelectedListings);

        return () => {
            bulkActionDropdownNew?.removeListener(updateSelectedListings);
            bulkActionDropdownNew.selectedListings = [];
        }
    }, []);

    if (!bulkActionDropdownNew.selectedListings && !status) {
        return <SkeletonComponent />
    }

    return (
        <div className={styles.dropdownContainer}>
            <ButtonNew
                variant='secondary-gray'
                ref={dropdownButtonRef}
                iconAfter={<ArrowDownSLineIcon className={classNames(styles.arrowIcon, isDropdownActive ? styles.open : styles.closed)} />}
                className={styles.dropdownButton}
                disabled={(bulkActionDropdownNew.selectedListings.length < 2)}
                onClick={handleDropdownButtonClick}
            >
                {
                    (selectedListings.length > 1) ?
                        t('listings.bulkActions.caption.amount', {
                            amountOfItems: bulkActionDropdownNew.selectedListings.length
                        }) :
                        t('listings.bulkActions.caption')
                }
            </ButtonNew>
            <Dropdown
                align='left'
                isActive={isDropdownActive}
            >
                <div className={styles.dropdownContent} ref={dropdownContentRef}>
                    <div className={classNames(styles.itemsSelected, styles.dropdownItem)}>
                        <Text
                            color={ColorEnum.Gray800}
                            weight={FontWeightEnum.Semibold}
                            size={FontSizeEnum.Sm}
                        >
                            <Trans
                                i18nKey={'listings.bulkActions.amount'}
                                values={{ amountOfItems: selectedListings.length }}
                                components={{ strong: <strong /> }}
                            />
                        </Text>
                    </div>

                    {(status === ListingStatus.Open) &&
                        <ButtonNew
                            className={styles.dropdownItem}
                            variant='empty'
                            onClick={() => ArchiveListingModal.open(null, true, selectedListings, () => { /**/ }, false, false)}
                        >
                            {t('actions.archive')}
                        </ButtonNew>
                    }

                    {(status === ListingStatus.Archived) &&
                        <ButtonNew
                            className={styles.dropdownItem}
                            variant='empty'
                            onClick={() => UnarchiveListingModal.open(null, true, selectedListings, null, () => { /**/ })}
                        >
                            {t('actions.unarchive')}
                        </ButtonNew>
                    }
                </div>
            </Dropdown>
        </div>
    );
}

Object.assign(BulkActionDropdownNew, bulkActionDropdownNew);
export default (BulkActionDropdownNew as BulkActionDropdownComponent & BulkActionEventManager<BulkActionEventData>);
